import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

const BlogPost16: React.FC = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');

  const handleGenerateName = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim() && surname.trim()) {
      navigate('/chinese-name-generator', { 
        state: { 
          firstName: firstName.trim(),
          surname: surname.trim(),
          autoGenerate: true 
        } 
      });
    }
  };

  return (
    <>
      <SeoMeta
        title="How to Say My Name Is in Chinese | Complete Guide"
        description="Need to introduce yourself in Chinese? Learn how to say 'My name is' (我叫...) and 'What's your name?' (你叫什么名字?) with proper pronunciation. Master formal and casual introductions, understand cultural etiquette, and practice with audio examples. Includes common phrases, tone tips, and how to introduce your Chinese name confidently. Perfect for students, travelers, and business professionals meeting Chinese speakers."
        keywords="how to say my name is in chinese, how do you say my name is in chinese, my name is in chinese, introduce yourself in chinese, chinese introduction phrases"
        pathname="/blog/how-to-say-my-name-in-chinese"
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50">
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </nav>

        <header className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Tutorial Guide
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How to Say "My Name Is" in Chinese
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Learn the proper way to introduce yourself in Chinese. This guide covers pronunciation, common phrases, and cultural considerations for saying <strong>"my name is in Chinese"</strong> correctly.
            </p>
          </div>
        </header>

        <article className="max-w-4xl mx-auto px-4 pb-16">
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Common Phrases for Introducing Your Name</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              When learning <strong>how to say my name is in Chinese</strong>, there are several phrases you can use depending on the formality of the situation. Here are the most common ways to introduce yourself:
            </p>
            <div className="space-y-6">
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">我叫... (Wǒ jiào...)</h3>
                <p className="text-gray-700 mb-3">
                  This is the most common and casual way to say "My name is..." in Chinese. It's perfect for everyday conversations and informal settings.
                </p>
                <p className="text-gray-700 font-medium">Example:</p>
                <p className="text-gray-700">我叫李明。 (Wǒ jiào Lǐ Míng.) - My name is Li Ming.</p>
              </div>
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">我的名字是... (Wǒ de míngzì shì...)</h3>
                <p className="text-gray-700 mb-3">
                  A more formal way to introduce your name. Use this in professional settings or when you want to be more polite.
                </p>
                <p className="text-gray-700 font-medium">Example:</p>
                <p className="text-gray-700">我的名字是李明。 (Wǒ de míngzì shì Lǐ Míng.) - My name is Li Ming.</p>
              </div>
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">我是... (Wǒ shì...)</h3>
                <p className="text-gray-700 mb-3">
                  This means "I am..." and can be used to introduce yourself, though it's less common for name introductions.
                </p>
                <p className="text-gray-700 font-medium">Example:</p>
                <p className="text-gray-700">我是李明。 (Wǒ shì Lǐ Míng.) - I am Li Ming.</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Pronunciation Guide</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              When learning <strong>how do you say my name is in chinese</strong>, proper pronunciation is crucial. Here's a breakdown:
            </p>
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-gray-900 mb-2">我 (Wǒ) - "I" or "me"</p>
                <p className="text-gray-600">Pronounced with a falling-rising tone (3rd tone)</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-2">叫 (jiào) - "called" or "named"</p>
                <p className="text-gray-600">Pronounced with a falling tone (4th tone)</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-2">名字 (míngzì) - "name"</p>
                <p className="text-gray-600">Both characters use neutral tone in this context</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cultural Tips</h2>
            <ul className="list-disc list-inside space-y-3 text-gray-700">
              <li>In Chinese culture, it's common to state your surname first, then your given name</li>
              <li>When introducing yourself, you can add "很高兴认识你" (hěn gāoxìng rènshí nǐ) meaning "nice to meet you"</li>
              <li>In formal situations, you might want to add your title or position after your name</li>
              <li>Practice the tones carefully, as incorrect tones can change the meaning</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Get Your Chinese Name</h2>
            <form onSubmit={handleGenerateName} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  className="px-4 py-2 border border-gray-300 rounded-md"
                  required
                />
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Last Name"
                  className="px-4 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors font-medium"
              >
                Generate Chinese Name →
              </button>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/blog/chinese-names-pronunciation" className="text-green-600 hover:underline font-medium">
                  How to Pronounce Chinese Names →
                </Link>
              </li>
              <li>
                <Link to="/" className="text-green-600 hover:underline font-medium">
                  Chinese Name Generator →
                </Link>
              </li>
            </ul>
          </div>
        </article>
      </div>
    </>
  );
};

export default BlogPost16;

